import { Trans, t } from "@lingui/macro";
import React, { useEffect } from "react";
import useSWR from "swr";
import cx from "classnames";
import Button from "components/Button/Button";
import FromInput from "pages/Event/components/FromInput";
import ToInput from "pages/Event/components/ToInput";
import { useProgressDisplayed } from "pages/Event/hooks/utils/useDisplayedProgress";
import BuyPlpSuccessModal from "pages/Event/components/BuyPlpSuccessModal";
import { Tooltip } from "components/TooltipV2/Tooltip";
import { TipIcon } from "components/TipIcon";
import { roundToFixed } from "lib/roundToFixed";
import { formatAmount } from "pages/Event/utils/formatAmount";
import { BigNumber, constants } from "ethers";
import { formatUnitsPlp } from "pages/Event/utils/formatUnitsPlp";
import { useEventGetAmountsOut } from "pages/Event/hooks/blockchain/useEventGetAmountsOut";
import { formatUnits, parseUnits } from "@ethersproject/units";
import { PALM_DECIMALS, PLP_DECIMALS } from "pages/Event/hooks/constants";
import { useEventGetAmountsIn } from "pages/Event/hooks/blockchain/useEventGetAmountsIn";
import { useStringAsBn } from "pages/Event/hooks/utils/useStringAsBn";
import { isZeroLike } from "pages/Event/hooks/utils/isZeroLike";
import { useBnAsString } from "pages/Event/hooks/utils/useBnAsString";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "lib/chains";
import { getContract } from "config/contracts";
import { useTokenAllowance } from "pages/Event/hooks/blockchain/useTokenAllowance";
import { useCollateralTokenAddress } from "lib/useCollateralTokenAddress";
import { useTokenBalance } from "pages/Event/hooks/blockchain/useTokenBalance";
import { useEventPurchasePlp } from "pages/Event/hooks/blockchain/useEventPurchasePlp";
import { getExplorerUrl, isSupportedChain } from "config/chains";
import { useProgress } from "../hooks/utils/useProgress";
import { TierDetails } from "../Event";
import { getTokenBySymbol } from "config/tokens";
import { helperToast } from "lib/helperToast";
import ExternalLink from "components/ExternalLink/ExternalLink";
import { usePlpPrice } from "pages/Event/hooks/blockchain/usePlpPrice";
import { usePlpBalance } from "pages/Event/hooks/blockchain/usePlpBalance";
import { contractFetcher } from "lib/contracts";
import LiquidityEvent from "abis/events/LiquidityEvent.json";
import { pushAccessNotification } from "./AccessNotification";

type Props = React.ComponentProps<"div"> & {
  currentTier: TierDetails;
  currentAmount: BigNumber;
  tiersDetails: TierDetails[];
  isNotActive?: boolean;
  connectWallet: () => {};
};

type CalculateOrigin = "from" | "to" | null;

type FormAction = "connect" | "approve" | "buy";

const BuyPlpFromCard = (props: Props) => {
  const { library, active, account } = useWeb3React();
  const { chainId } = useChainId();

  const liquidityEventAddress = getContract(chainId, "LiquidityEvent");
  const { data: isWhitelisted } = useSWR(
    account
      ? [`LiquidityEvent:isUserWhiteListed:${active}`, chainId, liquidityEventAddress, "isWhitelisted", account]
      : null,
    contractFetcher(library, LiquidityEvent)
  );

  const [isSuccessModalVisible, setIsSuccessModalVisible] = React.useState(false);

  const { tiersDetails, currentTier, currentAmount } = props;

  const { progress: tokensToNextTierProgress } = useProgress(
    currentTier.startAmount,
    currentTier.endAmount,
    currentAmount
  );

  const tokensToNextTier = React.useMemo(() => {
    const result = currentTier.endAmount.sub(currentAmount);

    if (result.lt(0)) {
      return BigNumber.from(0);
    }
    return result;
  }, [currentAmount, currentTier.endAmount]);

  const [fromAmount, setFromAmount] = React.useState("");
  const [toAmount, setToAmount] = React.useState("");
  const fromAmountOld = React.useRef(fromAmount);
  const toAmountOld = React.useRef(fromAmount);

  const setFromAmountForce = (amount: string) => {
    fromAmountOld.current = amount;
    setFromAmount(amount);
  };
  const setToAmountForce = (amount: string) => {
    toAmountOld.current = amount;
    setToAmount(amount);
  };

  const [calculateOrigin, setCalculateOrigin] = React.useState<CalculateOrigin>(null);
  const calculateOriginOld = React.useRef(calculateOrigin);

  const fromAmountBn = useStringAsBn(fromAmount, PLP_DECIMALS);
  const toAmountBn = useStringAsBn(toAmount, PALM_DECIMALS);

  // const estimatedApr = React.useMemo(() => {
  //   let estimatedApr = currentTier.estimatedApr;
  //   const amountAfter = toAmountBn.add(currentAmount);
  //   if (amountAfter.gt(currentTier.endAmount)) {
  //     // current pool amount after buy
  //     const tiersInAmount = tiersDetails.filter(
  //       (tier) => tier.tier.gt(currentTier.tier) && tier.startAmount.lt(amountAfter)
  //     ); // tiers within buy amount
  //     const totalApr = tiersInAmount.reduce((acc, tier) => acc.add(tier.estimatedApr), estimatedApr);
  //     estimatedApr = totalApr.div(tiersInAmount.length + 1); // average APR if the buy will fall into several tiers
  //   }
  //   return estimatedApr;
  // }, [currentTier.estimatedApr, currentTier.endAmount, currentTier.tier, toAmountBn, currentAmount, tiersDetails]);

  // Fetch amountsOut
  const amountsOutQuery = useEventGetAmountsOut(fromAmountBn, {
    isPaused: calculateOrigin !== "from" || isZeroLike(fromAmount),
  });
  const amountsOutNext = useBnAsString(amountsOutQuery.query.data?.amountsOut, PALM_DECIMALS);

  // Fetch amountsIn
  const amountsInQuery = useEventGetAmountsIn(toAmountBn, {
    isPaused: calculateOrigin !== "to" || isZeroLike(toAmount),
  });
  const amountsInNext = useBnAsString(amountsInQuery.query.data?.amountsIn, PLP_DECIMALS);

  // Set calculate origin
  useEffect(() => {
    if (fromAmount !== fromAmountOld.current) {
      setCalculateOrigin("from");
      fromAmountOld.current = fromAmount;
      return;
    }

    if (toAmount !== toAmountOld.current) {
      setCalculateOrigin("to");
      toAmountOld.current = toAmount;
      return;
    }
  }, [fromAmount, toAmount]);

  useEffect(() => {
    calculateOriginOld.current = calculateOrigin;
  }, [calculateOrigin]);

  // Sync zero toAmount
  useEffect(() => {
    if (isZeroLike(fromAmount) && calculateOrigin === "from") {
      setToAmountForce("0");
    }
  }, [fromAmount, calculateOrigin]);

  // Sync dynamic toAmount
  useEffect(() => {
    if (calculateOrigin !== "from") {
      return;
    }
    if (!amountsOutNext || isZeroLike(amountsOutNext)) {
      return;
    }
    if (amountsOutQuery.isPending) {
      return;
    }
    setToAmountForce(amountsOutNext);
  }, [calculateOrigin, amountsOutNext, amountsOutQuery.isPending]);

  // Sync zero fromAmount
  useEffect(() => {
    if (isZeroLike(toAmount) && calculateOrigin === "to") {
      setFromAmountForce("0");
    }
  }, [toAmount, calculateOrigin]);

  // Sync dynamic fromAmount
  useEffect(() => {
    if (calculateOrigin !== "to") {
      return;
    }
    if (!amountsInNext || isZeroLike(amountsInNext)) {
      return;
    }
    if (amountsInQuery.isPending) {
      return;
    }

    setFromAmountForce(amountsInNext);
  }, [calculateOrigin, amountsInNext, amountsInQuery.isPending]);

  const plpManagerAddress = getContract(chainId, "PlpManager");

  const COLLATERAL_TOKEN_DECIMALS = getTokenBySymbol(chainId, "USDT").decimals;

  const collateralTokenAddress = useCollateralTokenAddress();

  // Balance
  const tokenBalanceQuery = useTokenBalance(collateralTokenAddress, account ?? constants.AddressZero);
  const balanceBn = tokenBalanceQuery.balance;
  const balance = React.useMemo(
    () => (balanceBn ? formatUnits(balanceBn, COLLATERAL_TOKEN_DECIMALS) : undefined),
    [balanceBn, COLLATERAL_TOKEN_DECIMALS]
  );

  // Allowance
  const tokenAllowanceQuery = useTokenAllowance(
    collateralTokenAddress,
    account ?? constants.AddressZero,
    plpManagerAddress
  );

  // Purchase
  const eventPurchasePlp = useEventPurchasePlp();

  // Form state
  const formState = React.useMemo<
    {
      buttonText;
    } & ({ isSubmitDisabled: true } | { isSubmitDisabled: false; action: FormAction })
  >(() => {
    if (!active) {
      return {
        buttonText: t`Connect wallet`,
        isSubmitDisabled: false,
        action: "connect",
      };
    }
    if (props.isNotActive) {
      return {
        buttonText: t`Event is not active`,
        isSubmitDisabled: true,
      };
    }
    if (!isSupportedChain(chainId)) {
      return {
        buttonText: t`Incorrect Network`,
        isSubmitDisabled: true,
      };
    }
    // Inputs loadings
    if (
      amountsInQuery.isPending ||
      amountsOutQuery.isPending ||
      eventPurchasePlp.isMutating ||
      (isZeroLike(fromAmount) && !isZeroLike(toAmount)) ||
      (!isZeroLike(fromAmount) && isZeroLike(toAmount))
    ) {
      return {
        buttonText: t`Loading...`,
        isSubmitDisabled: true,
      };
    }
    if (!calculateOrigin || isZeroLike(fromAmount) || isZeroLike(toAmount)) {
      return {
        buttonText: t`Enter an amount`,
        isSubmitDisabled: true,
      };
    }
    // Other loadings
    if (tokenAllowanceQuery.isPending || !balanceBn || eventPurchasePlp.isMutating) {
      return {
        buttonText: t`Loading...`,
        isSubmitDisabled: true,
      };
    }

    if (!isWhitelisted) {
      return {
        buttonText: t`Not whitelisted`,
        isSubmitDisabled: true,
        action: "buy",
      };
    }

    if (balanceBn.lt(fromAmountBn)) {
      return {
        buttonText: t`Not enough balance`,
        isSubmitDisabled: true,
      };
    }
    if (tokenAllowanceQuery.haveEnoughAllowance(fromAmountBn)) {
      return {
        buttonText: t`Buy PLP`,
        isSubmitDisabled: false,
        action: "buy",
      };
    }
    if (!tokenAllowanceQuery.haveEnoughBalanceToApprove(balanceBn, fromAmountBn)) {
      return {
        buttonText: t`Not enough balance`,
        isSubmitDisabled: true,
      };
    }

    return {
      buttonText: t`Approve`,
      isSubmitDisabled: false,
      action: "approve",
    };
  }, [
    isWhitelisted,
    calculateOrigin,
    fromAmount,
    toAmount,
    amountsInQuery.isPending,
    amountsOutQuery.isPending,
    fromAmountBn,
    balanceBn,
    tokenAllowanceQuery,
    active,
    chainId,
    eventPurchasePlp.isMutating,
    props.isNotActive,
  ]);

  const normalizedRewardPerToken = React.useMemo(() => {
    let currentRewardPerToken = tokensToNextTier.eq(0) ? BigNumber.from(0) : currentTier.rewardPerToken;
    const amountAfter = toAmountBn.add(currentAmount);
    if (amountAfter.gt(currentTier.endAmount)) {
      const tiersInAmount = tiersDetails.filter(
        (tier) => tier.tier.gt(currentTier.tier) && tier.startAmount.lt(amountAfter)
      ); // tiers within buy amount
      const totalRewardPerToken = tiersInAmount.reduce(
        (acc, tier) => acc.add(tier.rewardPerToken),
        currentRewardPerToken
      );
      currentRewardPerToken = totalRewardPerToken.div(tiersInAmount.length + 1); // average reward per token if the buy will fall into several tiers
    }
    return currentRewardPerToken;
  }, [
    tokensToNextTier,
    currentTier.rewardPerToken,
    currentTier.endAmount,
    currentTier.tier,
    toAmountBn,
    currentAmount,
    tiersDetails,
  ]);

  const plpPrice = usePlpPrice();
  const plpBalance = usePlpBalance();

  const detailsList = React.useMemo(() => {
    return [
      {
        label: t`PLP price`,
        value: `$${formatAmount(plpPrice.formatted, { mantissa: 2, trimMantissa: false })}`,
      },
      {
        label: t`Rewards Rate`,
        tooltipText: t`Reward Rate shows the PALM amount received per PLP`,
        value: `${formatAmount(formatUnitsPlp(normalizedRewardPerToken))} PALM / PLP`,
      },
      // {
      //   label: t`Estimated current APR`,
      //   tooltipText: t`Estimated current APR shows the APR of the current tier`,
      //   value: `${formatAmount(estimatedApr.toNumber())}%`,
      // },
      {
        label: t`Bonus PALM Earning`,
        tooltipText: t`Bonus PALM earning shows how much PALM you get for your minted PLP`,
        value: `${formatAmount(
          formatUnits(
            normalizedRewardPerToken.mul(parseUnits(toAmount || "0", PLP_DECIMALS)),
            PALM_DECIMALS + PLP_DECIMALS
          )
        )} PALM`,
      },
      {
        label: t`Mint fee`,
        value: `0.3%`,
      },
    ] satisfies { label: string; value: string; tooltipText?: React.ReactNode }[];
  }, [normalizedRewardPerToken, toAmount, plpPrice.formatted]);

  const { progress: tokensToNextTierProgressDisplayed } = useProgressDisplayed(
    tokensToNextTierProgress,
    0.01 / 100 // 0.01%
  );

  React.useEffect(() => {
    if (isWhitelisted === false) {
      pushAccessNotification();
    }
  }, [isWhitelisted]);

  const onFormSubmit = async (e) => {
    e.preventDefault();
    try {
      // Main
      if (formState.isSubmitDisabled) {
        return;
      }
      if (formState.action === "connect") {
        props.connectWallet();
        return;
      }

      if (formState.action === "approve") {
        const tx = await tokenAllowanceQuery.approve(fromAmountBn);
        const txUrl = getExplorerUrl(chainId) + "tx/" + tx.hash;
        helperToast.success(
          <div>
            <Trans>
              Approval submitted! <ExternalLink href={txUrl}>View status.</ExternalLink>
            </Trans>
            <br />
          </div>
        );

        return;
      }

      if (formState.action === "buy") {
        await eventPurchasePlp.purchasePlp(fromAmountBn, toAmountBn, toAmountBn, () => {
          // After
          setIsSuccessModalVisible(true);
          setCalculateOrigin(null);
          setFromAmountForce("");
          setToAmountForce("");
        });

        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  };

  return (
    <>
      <div
        className={cx([
          "rounded-[2.5rem] border bg-background-5-v2-only @container/card relative overflow-hidden rounded-[2.5rem] p-[2.5rem]",
          props.className,
        ])}
        key="card"
      >
        <div className="flex justify-between flex-col gap-6 @[50rem]/card:flex-row @[50rem]/gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-[2.2rem] leading-[1.4] font-extrabold font-mazzard">
              <Trans>Buy PLP for Hyper Rewards</Trans>
            </p>

            <p className="text-inactive text-[1.2rem] leading-[1.5]">
              <Trans>{formatAmount(formatUnitsPlp(tokensToNextTier))} PLP left to next tier</Trans>
            </p>
          </div>

          <p className="text-[2.4rem] leading-[1.4] font-extrabold font-mazzard text-[#e1d9fb]">
            <Trans>{roundToFixed((1 - tokensToNextTierProgressDisplayed) * 100, 2)}% LEFT</Trans>
          </p>
        </div>

        <div className="mt-10 flex flex-col items-stretch ">
          <form onSubmit={onFormSubmit} noValidate>
            <fieldset className="gap-4 flex flex-col ">
              <FromInput
                balance={balance}
                amount={fromAmount}
                onChangeAmount={setFromAmount}
                disabled={amountsInQuery.isPending || eventPurchasePlp.isMutating}
                decimals={COLLATERAL_TOKEN_DECIMALS}
              />

              <ToInput
                plpBalance={plpBalance.formatted}
                amount={toAmount}
                onChangeAmount={setToAmount}
                disabled={amountsOutQuery.isPending || eventPurchasePlp.isMutating}
              />

              <Button
                variant="primary-action"
                size="lg"
                className="w-full"
                disabled={formState.isSubmitDisabled}
                type="submit"
              >
                {formState.buttonText}
              </Button>
            </fieldset>
          </form>

          <ul className="mt-6 flex flex-col gap-2 @[30rem]/gap-2">
            {detailsList.map((el) => (
              <li className="grid grid-rows-2 gap-0 @[30rem]/card:grid-rows-1 @[30rem]/card:grid-cols-2" key={el.label}>
                <div className="flex gap-2 items-center">
                  <div className="text-[1.4rem] leading-[1.5] text-inactive flex items-center">{el.label}</div>

                  {el.tooltipText && (
                    <Tooltip renderContent={() => el.tooltipText} disabled={!el.tooltipText}>
                      <TipIcon />
                    </Tooltip>
                  )}
                </div>

                <div className="@[30rem]/card:text-right text-[1.4rem] leading-[1.5] text-white font-medium">
                  <Trans>{el.value}</Trans>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <BuyPlpSuccessModal isVisible={isSuccessModalVisible} setIsVisible={setIsSuccessModalVisible} />
    </>
  );
};

export default BuyPlpFromCard;
